(() => {
  const elementName = 'cf-product-features';
  const maximumAddedSequences = 100;

  if (customElements.get(elementName)) return;

  class CFProductFeatures extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.hasAttribute('data-cf-features-enabled')) return;

      this.initialized = true;
      this.viewport = this.querySelector('[data-cf-features-viewport]');
      this.primaryGroup = this.querySelector('[data-cf-features-group]');
      this.originalSequence = this.querySelector('[data-cf-features-sequence]');
      this.duplicateGroup = this.querySelector('[data-cf-features-clone]');

      if (!this.viewport || !this.primaryGroup || !this.originalSequence || !this.duplicateGroup) {
        this.initialized = false;
        return;
      }

      this.speed = this.readNumber(this.dataset.cfFeaturesSpeed, 40, 20, 100);
      this.frame = 0;
      this.handleWindowResize = () => this.scheduleUpdate();
      this.handleMotionChange = () => this.syncMotionPreference();
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (typeof this.motionPreference.addEventListener === 'function') {
        this.motionPreference.addEventListener('change', this.handleMotionChange);
      } else if (typeof this.motionPreference.addListener === 'function') {
        this.motionPreference.addListener(this.handleMotionChange);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.handleWindowResize, { passive: true });
      }

      this.syncMotionPreference();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected && this.initialized) this.scheduleUpdate();
        });
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.handleWindowResize);

      if (typeof this.motionPreference?.removeEventListener === 'function') {
        this.motionPreference.removeEventListener('change', this.handleMotionChange);
      } else if (typeof this.motionPreference?.removeListener === 'function') {
        this.motionPreference.removeListener(this.handleMotionChange);
      }

      this.initialized = false;
    }

    readNumber(value, fallback, minimum, maximum) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(maximum, Math.max(minimum, parsed));
    }

    syncMotionPreference() {
      if (!this.motionPreference.matches) this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (!this.initialized || this.motionPreference.matches) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateMarquee());
    }

    updateMarquee() {
      if (!this.isConnected || !this.initialized) return;

      this.ensureCoverage();
      const groupWidth = this.primaryGroup.getBoundingClientRect().width;
      if (groupWidth > 0) this.style.setProperty('--cf-features-duration', `${groupWidth / this.speed}s`);
    }

    ensureCoverage() {
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      const sequenceWidth = this.originalSequence.getBoundingClientRect().width;
      const groupWidth = this.primaryGroup.getBoundingClientRect().width;
      if (viewportWidth <= 0 || sequenceWidth <= 0 || groupWidth >= viewportWidth) return;

      const copies = Math.min(maximumAddedSequences, Math.ceil((viewportWidth - groupWidth) / sequenceWidth));
      for (let index = 0; index < copies; index += 1) {
        const primaryCopy = this.originalSequence.cloneNode(true);
        primaryCopy.removeAttribute('data-cf-features-sequence');
        primaryCopy.setAttribute('data-cf-features-filler', '');
        primaryCopy.setAttribute('aria-hidden', 'true');
        this.cleanClone(primaryCopy);

        const duplicateCopy = primaryCopy.cloneNode(true);
        this.primaryGroup.append(primaryCopy);
        this.duplicateGroup.append(duplicateCopy);
      }
    }

    cleanClone(element) {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
      element.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      element.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
    }
  }

  customElements.define(elementName, CFProductFeatures);
})();
